import { IconDrag } from '@hulu-react-style-components/icons';
import { colorGray5 } from '@hulu/design-tokens/lib/colors.common';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import ReactTooltip from 'react-tooltip';

import type { DropdownOption } from '../../../common/Dropdown';
import EditableDropdown from '../../../common/EditableTableCells/EditableDropdown';
import { adTypes } from '../../../configs';
import { stringToDateTimeString } from '../../Trafficking/TraffickingPage/columns';
import {
  SEQUENCE_ROW_TOOLTIP_DELAY_HIDE,
  SEQUENCE_ROW_TOOLTIP_DELAY_SHOW,
  SEQUENCE_ROW_TOOLTIP_MESSAGE,
  SEQUENCE_ROW_TOOLTIP_PLACEMENT,
} from '../constants';
import { getAdSelectorOrder } from '../hooks/utils';
import { POD_POSITION_OPTIONS } from '../podPositionConstants';
import { getDefaultSlotPosition, SLOT_POSITION_OPTIONS, UNSELECTED_SLOT_POSITION } from '../slotPositionConstants';
import type { SequenceAdV5, SequenceFormValues } from '../types';

type SequenceTableRowProps = {
  row: SequenceAdV5;
  tableIndex: number;
  rowIndex: number;
  rowErrors?: string[];
};

const SequenceTableRow = ({ row, tableIndex, rowIndex, rowErrors }: SequenceTableRowProps): React.JSX.Element => {
  const { isSubmitting, setValues } = useFormikContext<SequenceFormValues>();

  const isDropdownDisabled = isSubmitting || !!row.adRotation || !row.adSequence;

  const podPositionOptions: DropdownOption[] = useMemo(
    () => POD_POSITION_OPTIONS.map(({ value, label }) => ({ value, label })),
    []
  );

  const selectedPodPosition: DropdownOption = useMemo(() => {
    const currentValue = row.adSequence?.podPosition ?? '';
    return podPositionOptions.find((opt) => opt.value === currentValue) ?? podPositionOptions[0];
  }, [row.adSequence?.podPosition, podPositionOptions]);

  const handlePodPositionChange = (value: string): void => {
    const newPodPosition = value || null;

    setValues((prev) => {
      const sequences = [...prev.sequences];
      if (!sequences[tableIndex]) return prev;

      const sequence = { ...sequences[tableIndex] };
      const ads = [...sequence.ads];
      const ad = { ...ads[rowIndex] };

      ad.adSequence = ad.adSequence ? { ...ad.adSequence, podPosition: newPodPosition } : null;

      ads[rowIndex] = ad;
      sequence.ads = ads;
      sequences[tableIndex] = sequence;

      return { ...prev, sequences };
    });
  };

  const handleSlotPositionChange = (value: string): void => {
    setValues((prev) => {
      const sequences = [...prev.sequences];
      const sequence = { ...sequences[tableIndex] };
      const ads = [...sequence.ads];
      const ad = { ...ads[rowIndex] };

      ad.adSequence = ad.adSequence ? { ...ad.adSequence, podAlignment: value } : null;

      ads[rowIndex] = ad;
      sequence.ads = ads;
      sequences[tableIndex] = sequence;

      return { ...prev, sequences };
    });
  };

  const slotPositionValue = row.adSequence
    ? row.adSequence.podAlignment ?? getDefaultSlotPosition(row.lineItem?.countries)
    : null;

  const slotPositionDropdownOptions: DropdownOption<string>[] = useMemo(
    () => SLOT_POSITION_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
    []
  );

  const selectedSlotPosition: DropdownOption<string> = useMemo(
    () => slotPositionDropdownOptions.find((opt) => opt.value === slotPositionValue) ?? UNSELECTED_SLOT_POSITION,
    [slotPositionValue, slotPositionDropdownOptions]
  );

  return (
    <>
      <Draggable isDragDisabled={!!row.adRotation || isSubmitting} draggableId={`ad-${row.id}`} index={rowIndex}>
        {(provided, snapshot): React.ReactElement => {
          const trStyle: React.CSSProperties = { ...provided?.draggableProps.style };
          // need to remove the left that react-beautiful-dnd puts on by default
          delete trStyle.left;
          // remove padding that is on there so it sits center of drag and drop
          trStyle.padding = '0px';

          if (snapshot.dropAnimation) {
            const animation = snapshot.dropAnimation;

            // we need to set the transform so that the drop animation does not go off screen
            trStyle.transform = `translate(0px, ${animation.moveTo.y}px)`;
          }

          if (row.adRotation) {
            trStyle.backgroundColor = colorGray5;
          }

          return (
            <tr
              ref={provided?.innerRef}
              {...provided?.draggableProps}
              {...provided?.dragHandleProps}
              style={trStyle}
              data-for={row.id}
              data-place={SEQUENCE_ROW_TOOLTIP_PLACEMENT}
              data-tip-disable={!row.adRotation}
              data-delay-show={SEQUENCE_ROW_TOOLTIP_DELAY_SHOW}
              data-delay-hide={SEQUENCE_ROW_TOOLTIP_DELAY_HIDE}
              data-tip={SEQUENCE_ROW_TOOLTIP_MESSAGE}
              data-html
            >
              <td>
                <IconDrag className={'icon-drag'} />
              </td>
              <td>
                {row.type === adTypes.AD_SELECTOR_SLATE.key || row.type === adTypes.AD_SELECTOR_VIDEO.key
                  ? getAdSelectorOrder(row.type)
                  : rowIndex + 1}
              </td>
              <td title={row.name}>{row.name}</td>
              <td>{row.type}</td>
              <td className="sequence-table__pod-position-cell">
                <EditableDropdown
                  selectedValue={selectedPodPosition}
                  options={podPositionOptions}
                  onSubmit={handlePodPositionChange}
                  variant="base"
                  isDisabled={isSubmitting || !!row.adRotation || !row.adSequence}
                  dataTestId={`pod-position-${tableIndex}-${rowIndex}`}
                  menuPortalTarget={document.body}
                  closeOnOuterScroll
                />
              </td>
              <td
                className="sequence-table__slot-position-cell"
                data-testid={`slot-position-${tableIndex}-${rowIndex}`}
              >
                <EditableDropdown<string>
                  classNameModifier="slot-position-cell"
                  selectedValue={selectedSlotPosition}
                  options={slotPositionDropdownOptions}
                  onSubmit={handleSlotPositionChange}
                  variant={isDropdownDisabled ? 'disabled' : 'base'}
                  isDisabled={isDropdownDisabled}
                  dataTestId={`slot-position-dropdown-${tableIndex}-${rowIndex}`}
                  menuPortalTarget={document.body}
                  closeOnOuterScroll
                />
              </td>
              <td>{stringToDateTimeString({ value: row.startDate })}</td>
              <td>{stringToDateTimeString({ value: row.endDate })}</td>
              <td>{row.trackingId}</td>
              <td title={row.lineItem?.name ?? ''}>{row.lineItem?.name}</td>
              <td>
                {/** Tooltip is placed there to avoid DOM nesting warning */}
                <ReactTooltip id={row.id} />
              </td>
            </tr>
          );
        }}
      </Draggable>
      {rowErrors && rowErrors.length > 0 && (
        <tr className="sequence-table__row-error-tr" data-testid={`row-error-${tableIndex}-${rowIndex}`}>
          <td colSpan={12}>
            {rowErrors.map((error) => (
              <span className="sequence-table__row-error" key={error}>
                {error}
              </span>
            ))}
          </td>
        </tr>
      )}
    </>
  );
};

export default SequenceTableRow;
